import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, TrendingUp, Calendar, Download, Filter } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Select, DatePicker, Table, Badge } from '../../components/common';
import { reportApi } from '../../services/api';
import './Reports.css';

export default function POReport() {
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['reports', 'purchase-orders', dateFrom, dateTo, statusFilter],
        queryFn: () => reportApi.getPurchaseOrders({ dateFrom, dateTo, status: statusFilter }),
        select: (res) => res.data?.data || []
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': 'default',
            'approved': 'info',
            'sent': 'info',
            'completed': 'success',
            'cancelled': 'danger'
        };
        return <Badge variant={config[status] || 'default'}>{status}</Badge>;
    };

    const columns = [
        { key: 'poNumber', title: 'PO Number', dataIndex: 'poNumber' },
        { key: 'vendor', title: 'Vendor', render: (_, row) => row.vendor?.name },
        { key: 'totalAmount', title: 'Total', dataIndex: 'totalAmount', align: 'right', render: (val) => formatCurrency(val) },
        { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', render: (date) => formatDate(date) },
        { key: 'status', title: 'Status', dataIndex: 'status', render: (status) => getStatusBadge(status) }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'approved', label: 'Approved' },
        { value: 'sent', label: 'Sent' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const handleExport = () => {
        // Export logic
        console.log('Exporting PO report...');
    };

    return (
        <div className="report-page">
            <PageHeader
                title="Purchase Order Report"
                subtitle="Analyze purchase order data"
                breadcrumbs={[
                    { label: 'Reports', path: '/reports' },
                    { label: 'Purchase Orders' }
                ]}
                actions={
                    <Button icon={Download} onClick={handleExport}>
                        Export
                    </Button>
                }
            />

            <Card>
                <CardBody>
                    <div className="report-filters">
                        <DatePicker
                            label="From"
                            value={dateFrom}
                            onChange={setDateFrom}
                        />
                        <DatePicker
                            label="To"
                            value={dateTo}
                            onChange={setDateTo}
                        />
                        <Select
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            options={statusOptions}
                        />
                        <Button icon={Filter} onClick={() => refetch()}>
                            Apply Filter
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <Card>
                <Table
                    columns={columns}
                    data={data || []}
                    loading={isLoading}
                    emptyText="No data found"
                    rowKey="id"
                />
            </Card>
        </div>
    );
}
